import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Box, Typography, Paper, Button, Grid, TextField, MenuItem, 
  Select, FormControl, InputLabel, Chip, OutlinedInput, Checkbox, ListItemText,
  Tabs, Tab, Divider, List, ListItem, ListItemIcon, Avatar, IconButton, InputBase,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Autocomplete, Card, CardContent,
  ListItemSecondaryAction, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CategoryIcon from '@mui/icons-material/Category';
import FlagIcon from '@mui/icons-material/Flag';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function TicketDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [itProjects, setItProjects] = useState([]); // Added state for IT Projects
  const [types, setTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignUser, setAssignUser] = useState('');
  const [assigningUser, setAssigningUser] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  
  // Chat State
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');

  // Needs State
  const [needs, setNeeds] = useState([]);
  const [newNeed, setNewNeed] = useState({ description: '', priority: 'Medium' });
  const [openNeedDialog, setOpenNeedDialog] = useState(false);
  const [openNeedCommentDialog, setOpenNeedCommentDialog] = useState(false);
  const [currentNeed, setCurrentNeed] = useState(null);
  const [needComment, setNeedComment] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'Open',
    requested_by: '',
    sender_email: '',
    project_id: '',
    ticket_type_id: '',
    assigned_to_ids: [],
    priority: 'Medium',
    resolution: '',
    due_date: '',
    done_at: '',
    created_at: ''
  });

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  const headers = { Authorization: `Bearer ${token}` };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Done': return 'success';
          case 'In Progress': return 'info';
          case 'Open': return 'warning';
          case 'Reopen': return 'error';
          default: return 'default';
      }
  };

  const getPriorityColor = (priority) => {
      switch(priority) {
          case 'Critical': return 'error';
          case 'High': return 'warning';
          case 'Medium': return 'info';
          case 'Low': return 'success';
          default: return 'default';
      }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, projectsRes, itProjectsRes, typesRes, countriesRes] = await Promise.all([
          axios.get(`${API_URL}/team`, { headers }),
          axios.get(`${API_URL}/projects`, { headers }),
          axios.get(`${API_URL}/it-projects`, { headers }),
          axios.get(`${API_URL}/ticket-types`, { headers }),
          axios.get(`${API_URL}/setup/countries`, { headers })
        ]);
        
        setUsers(usersRes.data);
        setProjects(projectsRes.data);
        setItProjects(itProjectsRes.data);
        setTypes(typesRes.data);
        setCountries(countriesRes.data);

        if (!isNew) {
          const ticketRes = await axios.get(`${API_URL}/tickets/${id}`, { headers });
          const attRes = await axios.get(`${API_URL}/tickets/${id}/attachments`, { headers });
          setAttachments(attRes.data);
          
          const chatRes = await axios.get(`${API_URL}/tickets/${id}/chat`, { headers });
          setChatMessages(chatRes.data);
          
          const needsRes = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
          setNeeds(needsRes.data);

          const t = ticketRes.data;
          
          // Helper to convert UTC string to Local ISO string for datetime-local input
          const toLocal = (s) => {
             if (!s) return '';
             // Append Z if missing to treat as UTC
             const d = new Date(s.endsWith('Z') ? s : s + 'Z');
             const offset = d.getTimezoneOffset() * 60000;
             return new Date(d.getTime() - offset).toISOString().slice(0, 16);
          };

          setFormData({
            title: t.title,
            description: t.description || '',
            status: t.status,
            requested_by: t.requested_by || '',
            sender_email: t.sender_email || '',
            project_id: t.project_id || '',
            it_project_id: t.it_project_id || '', // Added it_project_id
            ticket_type_id: t.ticket_type_id || '',
            country_id: t.country_id || '',
            assigned_to_ids: t.assigned_to_ids || [],
            priority: t.priority || 'Medium',
            resolution: t.resolution || '',
            due_date: t.due_date || '',
            done_at: toLocal(t.done_at),
            created_at: toLocal(t.created_at)
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAssignUser = async () => {
    if (assignUser && !formData.assigned_to_ids.includes(assignUser)) {
      const newAssignments = [...formData.assigned_to_ids, assignUser];
      setFormData({
        ...formData,
        assigned_to_ids: newAssignments
      });

      if (!isNew) {
        setAssigningUser(true);
        try {
          await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
        } catch (err) {
          console.error("Error updating assignments:", err);
          alert("Failed to update assignments");
        } finally {
          setAssigningUser(false);
        }
      }
    }
    setOpenAssignDialog(false);
    setAssignUser('');
  };

  const handleRemoveUser = async (userId) => {
    const newAssignments = formData.assigned_to_ids.filter(id => id !== userId);
    setFormData({
      ...formData,
      assigned_to_ids: newAssignments
    });

    if (!isNew) {
      try {
        await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
      } catch (err) {
        console.error("Error updating assignments:", err);
        alert("Failed to update assignments");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newFormData = { ...formData, [name]: value };

    if (name === 'status') {
        if (value === 'Done') {
            // Set done_at to current local datetime (YYYY-MM-DDTHH:mm)
            const now = new Date();
            now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
            newFormData.done_at = now.toISOString().slice(0, 16);
        } else {
            newFormData.done_at = '';
        }
    }
    setFormData(newFormData);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUpload = async (file) => {
      if (!file) return;

      const uploadData = new FormData();
      uploadData.append('file', file);

      try {
          await axios.post(`${API_URL}/tickets/${id}/attachments`, uploadData, { 
              headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
          });
          const res = await axios.get(`${API_URL}/tickets/${id}/attachments`, { headers });
          setAttachments(res.data);
      } catch (err) {
          console.error(err);
          const msg = err.response?.data?.error || 'Failed to upload file';
          alert(msg);
      }
  };
  
  const handleFileChange = (event) => {
      handleUpload(event.target.files[0]);
  };

  const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
  };

  const handleDragLeave = (e) => {
      e.preventDefault();
      setIsDragging(false);
  };

  const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
          handleUpload(files[0]);
      }
  };

  const handleAddChat = async () => {
    if (!newChatMessage.trim()) return;
    try {
      await axios.post(`${API_URL}/tickets/${id}/chat`, { message: newChatMessage }, { headers });
      setNewChatMessage('');
      const res = await axios.get(`${API_URL}/tickets/${id}/chat`, { headers });
      setChatMessages(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddNeed = async () => {
    try {
      await axios.post(`${API_URL}/tickets/${id}/needs`, newNeed, { headers });
      setOpenNeedDialog(false);
      setNewNeed({ description: '', priority: 'Medium' });
      const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
      setNeeds(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateNeedStatus = async (needId, newStatus) => {
      try {
          await axios.put(`${API_URL}/tickets/needs/${needId}`, { status: newStatus }, { headers });
          const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
          setNeeds(res.data);
      } catch (err) {
          console.error(err);
      }
  };

  const handleSendNeedComment = async () => {
      try {
          await axios.post(`${API_URL}/tickets/needs/${currentNeed.id}/comments`, { comment: needComment }, { headers });
          setOpenNeedCommentDialog(false);
          setNeedComment('');
          const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
          setNeeds(res.data);
      } catch (err) {
          console.error(err);
      }
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      
      // Convert local done_at back to UTC before sending
      if (payload.done_at) {
          const localDate = new Date(payload.done_at);
          payload.done_at = localDate.toISOString();
      }

      if (isNew) {
        payload.created_by_id = userId;
        await axios.post(`${API_URL}/tickets`, payload, { headers });
        navigate('/tickets');
      } else {
        await axios.put(`${API_URL}/tickets/${id}`, payload, { headers });
        // Keep in edit mode
      }
    } catch (err) {
      alert('Error saving ticket');
      console.error(err);
    }
  };

  if (loading) return <Loading />;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/tickets')} 
                        sx={{ color: 'rgba(255,255,255,0.8)', mb: 1, pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
                    >
                        Back to Tickets
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                        <InputBase
                            value={formData.title}
                            name="title"
                            onChange={handleChange}
                            placeholder="Ticket Title"
                            sx={{ 
                                color: 'inherit', 
                                fontSize: { xs: '1.5rem', md: '2rem' }, 
                                fontWeight: '800',
                                letterSpacing: -1,
                                '& .MuiInputBase-input': { p: 0, width: '100%' }
                            }}
                            fullWidth
                        />
                        <Chip 
                            label={formData.status} 
                            sx={{ 
                                bgcolor: formData.status === 'Done' ? '#4caf50' : 'white', 
                                color: formData.status === 'Done' ? 'white' : 'primary.main', 
                                fontWeight: 'bold',
                                height: 28,
                                boxShadow: 2
                            }} 
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', opacity: 0.9 }}>
                        {!isNew && (
                            <Typography variant="subtitle1" fontWeight="500">Ref: {`ACTS - T - ${String(id).padStart(5, '0')}`}</Typography>
                        )}
                        {/* Divider */}
                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'white' }} />
                        
                        {formData.created_at && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EventIcon fontSize="small" />
                                <Typography variant="subtitle1">Created: {new Date(formData.created_at).toLocaleDateString()}</Typography>
                            </Box>
                        )}
                        
                        {formData.requested_by && (
                            <>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'white' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon fontSize="small" />
                                    <Typography variant="subtitle1">Req: {formData.requested_by}</Typography>
                                </Box>
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button 
                            variant="contained" 
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.2)', 
                                backdropFilter: 'blur(10px)',
                                color: 'white', 
                                boxShadow: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } 
                            }}
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Box>

                {!isNew && (
                <Box sx={{ 
                    bgcolor: 'white', 
                    p: 1.5, 
                    borderRadius: 2, 
                    boxShadow: 3,
                    textAlign: 'center'
                }}>
                    <img 
                        src={`${API_URL}/tickets/${id}/qr`} 
                        alt="QR Code" 
                        style={{ width: 100, height: 100, display: 'block' }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                        SCAN ME
                    </Typography>
                </Box>
                )}
            </Box>
        </Box>
      </Paper>

      {/* Main Content Paper */}
      <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 2 }}
        >
            <Tab icon={<AssignmentIcon />} label="Ticket Details" iconPosition="start" sx={{ minHeight: 64 }} />
            <Tab icon={<PersonAddIcon />} label="Assign To" iconPosition="start" sx={{ minHeight: 64 }} />
            <Tab icon={<CheckCircleIcon />} label="Resolution" iconPosition="start" sx={{ minHeight: 64 }} />
            {!isNew && <Tab icon={<AttachFileIcon />} label="Documents" iconPosition="start" sx={{ minHeight: 64 }} />}
            {!isNew && <Tab icon={<ForumIcon />} label="Chat" iconPosition="start" sx={{ minHeight: 64 }} />}
            {!isNew && <Tab icon={<FlagIcon />} label="Needs" iconPosition="start" sx={{ minHeight: 64 }} />}
        </Tabs>

      {/* Tab 1: Ticket Details */}
      <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
                {/* Left Column: Description */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AssignmentIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold" color="text.primary">Description</Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <TextField 
                            fullWidth 
                            multiline 
                            minRows={16} 
                            name="description" 
                            value={formData.description} 
                            onChange={handleChange} 
                            placeholder="Enter detailed ticket description..."
                            variant="outlined"
                            sx={{ 
                                flex: 1,
                                '& .MuiOutlinedInput-root': { 
                                    height: '100%', 
                                    alignItems: 'flex-start',
                                    bgcolor: '#fafafa',
                                    borderRadius: 2
                                } 
                            }}
                        />
                    </Box>
                </Grid>

                {/* Right Column: Meta Data */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={4} sx={{ height: '100%' }}>
                        {/* Status & Type */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Classification</Typography>
                            <Stack spacing={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        label="Status"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Open"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} /> Open</Box></MenuItem>
                                        <MenuItem value="In Progress"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} /> In Progress</Box></MenuItem>
                                        <MenuItem value="Reopen"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} /> Reopen</Box></MenuItem>
                                        <MenuItem value="Done"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} /> Done</Box></MenuItem>
                                    </Select>
                                </FormControl>
                                
                                <FormControl fullWidth size="small">
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        name="priority"
                                        value={formData.priority}
                                        label="Priority"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Low">Low</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="High">High</MenuItem>
                                        <MenuItem value="Critical">Critical</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        name="ticket_type_id"
                                        value={formData.ticket_type_id}
                                        label="Type"
                                        onChange={handleChange}
                                    >
                                        {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Country</InputLabel>
                                    <Select
                                        name="country_id"
                                        value={formData.country_id}
                                        label="Country"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Box>
                        
                        <Divider />

                        {/* Context */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Full Context</Typography>
                            <Stack spacing={2}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={projects}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={projects.find(p => p.id === formData.project_id) || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            project_id: newValue ? newValue.id : ''
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Related Project" />}
                                />
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={itProjects}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={itProjects.find(p => p.id === formData.it_project_id) || null}
                                    onChange={(event, newValue) => {
                                        setFormData({
                                            ...formData,
                                            it_project_id: newValue ? newValue.id : ''
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Related IT Project" />}
                                />
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Due Date" 
                                    type="date"
                                    name="due_date" 
                                    value={formData.due_date} 
                                    onChange={handleChange} 
                                    InputLabelProps={{ shrink: true }}
                                />
                                {!isNew && (
                                    <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Created On</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {new Date(formData.created_at).toLocaleDateString()}
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                {new Date(formData.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        <Divider />

                        {/* People */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Contact Info</Typography>
                            <Stack spacing={2}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    size="small"
                                    options={users}
                                    getOptionLabel={(option) => option.name || option}
                                    value={users.find(u => u.name === formData.requested_by) || formData.requested_by}
                                    onChange={async (event, newValue) => {
                                        if (typeof newValue === 'string') {
                                            setFormData({ ...formData, requested_by: newValue });
                                            // Try to fetch from Azure AD if email-like
                                            if (newValue.includes('@')) {
                                                try {
                                                    const res = await axios.get(`${API_URL}/tickets/check-ad-user?email=${newValue}`, { headers });
                                                    if (res.data.found && res.data.country_id) {
                                                        setFormData(prev => ({ ...prev, country_id: res.data.country_id }));
                                                    }
                                                } catch (e) { console.log("AD Check failed", e); }
                                            }
                                        } else if (newValue) {
                                            let newCountryId = formData.country_id;
                                            if (newValue.countries && newValue.countries.length > 0) {
                                                const cName = newValue.countries[0];
                                                const cObj = countries.find(c => c.name === cName);
                                                if (cObj) newCountryId = cObj.id;
                                            }
                                            setFormData({ 
                                                ...formData, 
                                                requested_by: newValue.name,
                                                country_id: newCountryId
                                            });
                                        } else {
                                            setFormData({ ...formData, requested_by: '' });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Requested By"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <PersonIcon color="action" fontSize="small" sx={{ mr: 1, ml: 1 }} />
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Contact Email" 
                                    name="sender_email" 
                                    value={formData.sender_email} 
                                    onChange={handleChange} 
                                    InputProps={{
                                        startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>@</Typography>
                                    }}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
      </TabPanel>

      {/* Tab 2: Assign To */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">Assigned Team Members</Typography>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)}>
                Add Member
            </Button>
        </Box>
        <Grid container spacing={2}>
            {formData.assigned_to_ids.map((id) => {
                const member = users.find(u => u.id === id);
                if (!member) return null;
                return (
                  <Grid item xs={12} sm={6} key={member.id}>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }}>{member.name ? member.name[0] : '?'}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">{member.name}</Typography>
                          <Chip label={member.role || 'Member'} size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                        </Box>
                      </Box>
                      <IconButton size="small" color="error" onClick={() => handleRemoveUser(member.id)} sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'error.main', color: 'white' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                );
            })}
            {formData.assigned_to_ids.length === 0 && (
                <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <Typography color="textSecondary">No team members assigned yet.</Typography>
                </Box>
                </Grid>
            )}
        </Grid>
      </TabPanel>

      {/* Tab 3: Resolution */}
      <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">Resolution Details</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            multiline 
                            rows={8} 
                            label="Resolution Notes" 
                            name="resolution" 
                            value={formData.resolution} 
                            onChange={handleChange} 
                            placeholder="Describe how the ticket was resolved..."
                            variant="outlined"
                            sx={{ bgcolor: 'white' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField 
                            fullWidth 
                            type="datetime-local"
                            label="Completed On" 
                            name="done_at" 
                            value={formData.done_at} 
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField 
                            fullWidth 
                            label="Duration" 
                            value={(() => {
                                if (!formData.created_at || !formData.done_at) return '';
                                const start = new Date(formData.created_at);
                                const end = new Date(formData.done_at);
                                const diff = end - start;
                                if (diff < 0) return 'Invalid';
                                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                return `${days}d ${hours}h`;
                            })()} 
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Grid>
                </Grid>
            </Box>
      </TabPanel>

      {/* Tab 3: Documents */}
      {!isNew && (
        <TabPanel value={tabValue} index={3}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Attached Documents</Typography>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                >
                    Upload File
                    <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                    />
                </Button>
            </Box>

            <Box 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{ 
                    border: '2px dashed', 
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    borderRadius: 2, 
                    p: 4, 
                    mb: 3,
                    bgcolor: isDragging ? 'action.hover' : 'transparent',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'text.secondary', mb: 1 }} />
                <Typography variant="subtitle1" color="text.primary" fontWeight="medium">
                    {isDragging ? 'Drop file to upload' : 'Drag and drop file here'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    or click the upload button above
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {attachments.map((file) => (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                                <InsertDriveFileIcon />
                            </Avatar>
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <Typography variant="subtitle2" noWrap title={file.filename}>
                                    {file.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {new Date(file.uploaded_at).toLocaleDateString()} • {file.uploaded_by}
                                </Typography>
                            </Box>
                            <IconButton 
                                href={`${API_URL}/tickets/attachments/${file.id}`} 
                                color="primary"
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Paper>
                    </Grid>
                ))}
                
                {attachments.length === 0 && (
                    <Grid item xs={12}>
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography color="textSecondary">No documents attached yet.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </TabPanel>
      )}
      
      {/* Tab 4: Chat */}
      {!isNew && (
      <TabPanel value={tabValue} index={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                  <Box sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle1" fontWeight="bold">Ticket Discussion</Typography>
                          <Typography variant="caption" color="text.secondary">Use this channel for quick updates and team coordination.</Typography>
                      </Box>

                      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc' }}>
                          {chatMessages.length === 0 && (
                              <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.6 }}>
                                  <ForumIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                  <Typography variant="body2" color="text.secondary">No messages yet. Start the conversation!</Typography>
                              </Box>
                          )}
                          {chatMessages.map((msg, idx) => (
                              <Box key={msg.id || idx} sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 0.5 }}>
                                      <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: 'secondary.main' }}>{msg.user_name ? msg.user_name[0] : '?'}</Avatar>
                                      <Typography variant="caption" fontWeight="bold" color="text.primary">{msg.user_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">• {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                                  </Box>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'white', borderRadius: '4px 16px 16px 16px', border: '1px solid #e2e8f0', maxWidth: '85%' }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Typography>
                                  </Paper>
                              </Box>
                          ))}
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  placeholder="Type your message..."
                                  value={newChatMessage}
                                  onChange={e => setNewChatMessage(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddChat()}
                                  InputProps={{ sx: { borderRadius: 3, bgcolor: 'grey.50' } }}
                              />
                              <IconButton
                                  color="primary"
                                  onClick={handleAddChat}
                                  disabled={!newChatMessage.trim()}
                                  sx={{
                                      bgcolor: newChatMessage.trim() ? 'primary.main' : 'action.disabledBackground',
                                      color: 'white',
                                      width: 40, height: 40,
                                      '&:hover': { bgcolor: 'primary.dark' },
                                      '&.Mui-disabled': { color: 'white' }
                                  }}
                              >
                                  <SendIcon fontSize="small" />
                              </IconButton>
                          </Box>
                      </Box>
                  </Box>
              </CardContent>
          </Card>
      </TabPanel>
      )}

      {/* Tab 5: Needs */}
      {!isNew && (
      <TabPanel value={tabValue} index={5}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                <TextField
                    fullWidth size="small"
                    placeholder="Add new requirement..."
                    value={newNeed.description}
                    onChange={e => setNewNeed({ ...newNeed, description: e.target.value })}
                    InputProps={{ sx: { borderRadius: 2 } }}
                />
                {/* Due Date not in state yet, add if needed or remove from UI if not supported by backend */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                        value={newNeed.priority}
                        label="Priority"
                        onChange={(e) => setNewNeed({ ...newNeed, priority: e.target.value })}
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="Low">Low</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleAddNeed} startIcon={<AddIcon />} sx={{ borderRadius: 2, px: 3, height: 40 }} disabled={!newNeed.description}>Add</Button>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
                <CardContent sx={{ p: '0 !important' }}>
                    <List>
                        {needs && needs.length === 0 && <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No needs or requirements listed yet.</Typography></Box>}
                        {needs && needs.map((need, index) => (
                            <ListItem key={need.id} divider={index !== (needs ? needs.length - 1 : 0)} sx={{ py: 2 }}>
                                <ListItemIcon>
                                    <Checkbox
                                        checked={need.status === 'Met'}
                                        onChange={() => handleUpdateNeedStatus(need.id, need.status === 'Met' ? 'Pending' : 'Met')}
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body1" sx={{ textDecoration: need.status === 'Met' ? 'line-through' : 'none', color: need.status === 'Met' ? 'text.disabled' : 'text.primary', fontWeight: need.status === 'Met' ? 'normal' : 'medium' }}>
                                                    {need.description}
                                                </Typography>
                                                <Chip
                                                    label={need.priority}
                                                    size="small"
                                                    color={need.priority === 'High' ? 'error' : need.priority === 'Medium' ? 'warning' : 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 24, fontSize: '0.75rem', border: 'none', bgcolor: 'grey.100' }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                {need.created_at && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Created: {new Date(need.created_at).toLocaleDateString()}
                                                    </Typography>
                                                )}
                                                {/* need.due_date is not currently in GET /needs response, would need backend update if required */}
                                            </Box>
                                            {need.done_at && (
                                                <Typography variant="caption" color="success.main" display="block">
                                                    Completed: {new Date(need.done_at).toLocaleDateString()}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton size="small" onClick={() => {
                                        setCurrentNeed(need);
                                        setOpenNeedCommentDialog(true);
                                    }}>
                                        <CommentIcon fontSize="small" color={need.comments && need.comments.length > 0 ? 'primary' : 'action'} />
                                    </IconButton>
                                    {need.comments && need.comments.length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{need.comments.length}</Typography>
                                    )}
                                    {/* Delete functionality not requested but consistent with UI - placeholder */}
                                    {/* <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteNeed(need.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton> */}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>
      </TabPanel>
      )}

      </Paper>

      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
        <DialogTitle>Assign User</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>User</InputLabel>
            <Select value={assignUser} onChange={(e) => setAssignUser(e.target.value)} label="User">
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name} ({u.role || 'Member'})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained" disabled={assigningUser} startIcon={assigningUser ? <CircularProgress size={20} /> : null}>Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openNeedDialog} onClose={() => setOpenNeedDialog(false)}>
          <DialogTitle>Add Need / Requirement</DialogTitle>
          <DialogContent>
              <TextField 
                  fullWidth margin="dense" label="Description" 
                  value={newNeed.description} onChange={(e) => setNewNeed({ ...newNeed, description: e.target.value })} 
              />
              <FormControl fullWidth margin="dense">
                  <InputLabel>Priority</InputLabel>
                  <Select 
                      value={newNeed.priority} 
                      onChange={(e) => setNewNeed({ ...newNeed, priority: e.target.value })}
                      label="Priority"
                  >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                  </Select>
              </FormControl>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenNeedDialog(false)}>Cancel</Button>
              <Button onClick={handleAddNeed} variant="contained">Add</Button>
          </DialogActions>
      </Dialog>
      
            {/* Need Comment Dialog */}
            <Dialog open={openNeedCommentDialog} onClose={() => setOpenNeedCommentDialog(false)} maxWidth="sm" fullWidth >
                <DialogTitle>
                    Requirement Comments
                    <Typography variant="caption" display="block" color="text.secondary">{currentNeed?.description}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        {needs && currentNeed && needs.find(n => n.id === currentNeed.id)?.comments && needs.find(n => n.id === currentNeed.id)?.comments.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No comments yet.</Typography>
                        )}
                        {needs && currentNeed && needs.find(n => n.id === currentNeed.id)?.comments?.map(comment => (
                            <Box key={comment.id} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">{comment.user_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{new Date(comment.created_at).toLocaleString()}</Typography>
                                </Box>
                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2">{comment.comment}</Typography>
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a comment..."
                        value={needComment}
                        onChange={(e) => setNeedComment(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNeedCommentDialog(false)}>Close</Button>
                    <Button onClick={handleSendNeedComment} variant="contained" disabled={!needComment.trim()} endIcon={<SendIcon />}>Send</Button>
                </DialogActions>
            </Dialog>
    </Container>
  );
}

export default TicketDetails;
